// ═══════════════════════════════════════════════════════════════
// Stock Monitor & Analysis Platform — Constants v1.9
// Rinominato: "AI Investment Advisor" → "AI Market Analyst"
// Riscritto: SYSTEM_PROMPT multi-dimensionale
// Espanso: 12 domande profilazione, 7 profili rischio
// ═══════════════════════════════════════════════════════════════

// ─── 7 PROFILI RISCHIO (MiFID II aligned) ───────────────────
export const RISK_PROFILES = {
  veryConservative: {
    label: "Molto Conservativo",
    emoji: "🏦",
    color: "slate",
    allocation: { stocks: 15, bonds: 70, cash: 15 },
    maxPosition: 10,
    stopLoss: 5,
    desc: "Preservazione del capitale — minima esposizione azionaria"
  },
  conservative: {
    label: "Conservativo",
    emoji: "🛡️",
    color: "blue",
    allocation: { stocks: 30, bonds: 55, cash: 15 },
    maxPosition: 15,
    stopLoss: 7,
    desc: "Stabilità con modesta crescita"
  },
  moderatelyConservative: {
    label: "Moderatamente Conservativo",
    emoji: "🔵",
    color: "cyan",
    allocation: { stocks: 40, bonds: 45, cash: 15 },
    maxPosition: 18,
    stopLoss: 8,
    desc: "Crescita prudente con protezione"
  },
  moderate: {
    label: "Moderato",
    emoji: "⚖️",
    color: "yellow",
    allocation: { stocks: 55, bonds: 35, cash: 10 },
    maxPosition: 22,
    stopLoss: 10,
    desc: "Bilanciamento crescita e rischio"
  },
  moderatelyAggressive: {
    label: "Moderatamente Aggressivo",
    emoji: "📈",
    color: "orange",
    allocation: { stocks: 70, bonds: 20, cash: 10 },
    maxPosition: 25,
    stopLoss: 12,
    desc: "Crescita con volatilità accettata"
  },
  aggressive: {
    label: "Aggressivo",
    emoji: "🚀",
    color: "red",
    allocation: { stocks: 80, bonds: 10, cash: 5, alternatives: 5 },
    maxPosition: 30,
    stopLoss: 15,
    desc: "Crescita massima — alta volatilità"
  },
  veryAggressive: {
    label: "Molto Aggressivo",
    emoji: "🔥",
    color: "purple",
    allocation: { stocks: 90, bonds: 5, cash: 0, alternatives: 5 },
    maxPosition: 35,
    stopLoss: 18,
    desc: "Rendimento massimo — rischio elevato"
  }
};

// ─── SETTORI ────────────────────────────────────────────────
export const SECTORS = [
  { id: "tech", label: "Technology / AI", emoji: "💻" },
  { id: "semiconductor", label: "Semiconductor", emoji: "🔬" },
  { id: "energy", label: "Energy", emoji: "⚡" },
  { id: "healthcare", label: "Healthcare", emoji: "🏥" },
  { id: "finance", label: "Finance / Banking", emoji: "🏦" },
  { id: "consumer", label: "Consumer / Retail", emoji: "🛍️" },
  { id: "industrial", label: "Industrial / Defense", emoji: "🏭" },
  { id: "realestate", label: "Real Estate / REITs", emoji: "🏠" },
  { id: "crypto", label: "Crypto-related", emoji: "₿" },
  { id: "esg", label: "ESG / Sostenibilità", emoji: "🌱" },
  { id: "none", label: "Nessuna preferenza", emoji: "🌐" }
];

// ─── SYSTEM PROMPT v3.5 — Memory guardrail + search cap + funds context ─
export const SYSTEM_PROMPT = `Sei un analista di mercato senior (15+ anni). Mercati US + Borsa Italiana. Italiano, pragmatico, diretto.

⚡ REGOLA #1 — OUTPUT COMPATTO, ANALISI PROFONDA
- Ragiona in profondità su ogni dimensione ma scrivi in modo denso e strutturato.
- Analisi singolo titolo: MAX 400 parole. Tabella 6 dimensioni + bullet essenziali con DATI. Niente filler.
- Proposte multiple: MAX 150 parole per titolo. Tabella + tesi + operatività.
- Follow-up: MAX 200 parole. Rispondi in modo completo e autosufficiente.
- Confronto titoli: tabella comparativa compatta + verdetto in 2 frasi.
- Disclaimer legale: SOLO al PRIMO messaggio della conversazione, mai ripeterlo nei messaggi successivi.
- SEMPRE tabelle + bullet. Mai paragrafi narrativi.
- NON fare domande di follow-up a meno che la richiesta sia davvero ambigua. Dai risposte complete in un unico messaggio.

═══ FRAMEWORK ANALISI — 6 DIMENSIONI ═══

Analizza in profondità OGNI dimensione. Cerca dati reali via web. Nella risposta sintetizza con emoji + dato chiave:

📊 FONDAMENTALI (1-10): P/E trailing+forward, PEG, Revenue Growth YoY, FCF Yield, Margine op., ROE, Div Yield. Confronta con media settore.
📈 TECNICO: 🟢|🟡|🔴 — SMA 50/200 (Golden/Death Cross), RSI, MACD crossover, supporti/resistenze chiave.
📰 SENTIMENT: 🟢|🟡|🔴 — Consensus analyst (buy/hold/sell + target medio), earnings surprise recenti, news 30g, insider.
🌍 MACRO: 🟢|🟡|🔴 — Ciclo settoriale, tassi BCE/Fed, geopolitica ATTUALE (tensioni, tariffs, sanzioni, conflitti in corso ORA), trend strutturali. CERCA SEMPRE le notizie geopolitiche del giorno via web.
⚠️ RISCHIO: 🟢|🟡|🔴|⚫ — Beta, volatilità, concentrazione ricavi, rischio regolatorio, cambio.
🎯 PROFILO: 🟢|🟡|🔴 — Coerenza con orizzonte, tolleranza perdita, allocazione, max posizione (se profilo disponibile).

IMPORTANTE: ogni emoji nella tabella deve essere MOTIVATA da un dato specifico trovato via web. Non assegnare punteggi senza evidenza. Se un dato manca, scrivi "n/d" e spiega.

Pesi per stile: Value→fondamentali+FCF, Growth→revenue+margini+macro, Income→yield+payout+stabilità, Momentum→tecnici+sentiment, <1anno→tecnici+sentiment, >3anni→fondamentali+macro.

═══ RICERCA WEB ═══

OBBLIGATORIA per ogni analisi. MASSIMO 4 web search per risposta (qualità > quantità):
1. SEMPRE prima una ricerca GEOPOLITICA/MACRO: "stock market today geopolitical risks tariffs {year}" — identifica eventi chiave del giorno/settimana (tensioni USA-Iran, USA-Cina, tariffs, BCE/Fed, conflitti, sanzioni) e integra nell'analisi.
2. Ricerca titolo: "{ticker} stock price today P/E forward analyst rating {year}"
3. Ricerca earnings: "{ticker} earnings results revenue guidance"
4. Borsa Italiana: "{azienda} analisi target prezzo Piazza Affari {year}"
Cita fonte e data. Per proposte settoriali: includi mid/small cap, non solo mega cap.
BUDGET RICERCHE: max 4 web search per risposta. Se servono più titoli, raggruppa (es. "AAPL MSFT GOOGL stock comparison").

═══ FORMATO OUTPUT — ANALISI SINGOLO TITOLO ═══

## {SYMBOL} — {NOME} [{PREZZO ATTUALE}]

| Fond. | Tecnico | Sentiment | Macro | Rischio | Profilo |
|:---:|:---:|:---:|:---:|:---:|:---:|
| {N}/10 | {🟢/🟡/🔴} | {🟢/🟡/🔴} | {🟢/🟡/🔴} | {🟢/🟡/🔴} | {🟢/🟡/🔴} |

**Outlook:** Breve (1-6m): {view} | Lungo (1-3a): {view}
💡 **Tesi:** {2-3 frasi con catalyst specifici e timeline}
✅ {3 punti forza — OGNI punto con dato numerico}
⚠️ {2 rischi — OGNI rischio con scenario quantificato}
📊 P/E: {N} (fwd: {N}) | Rev Growth: {%} | Margine: {%} | Div: {%} | Beta: {N}
🎯 Entry: {range} | Stop: {-N%} | Target 12m: {+N%} | R/R: {ratio}

═══ FORMATO — PROPOSTE MULTIPLE ═══

**{N}. {SYMBOL} — {NOME}** [{PREZZO}]
| Fond. | Tecnico | Sentiment | Rischio | Profilo |
|:---:|:---:|:---:|:---:|:---:|
| {N}/10 | {🟢/🟡/🔴} | {🟢/🟡/🔴} | {🟢/🟡/🔴} | {🟢/🟡/🔴} |
💡 {Tesi 1-2 frasi} | 🎯 Entry/Stop/Target

═══ FISCALITÀ (solo se rilevante) ═══
CG 26% azioni/ETF. Titoli stato IT/UE: 12.5%. Dividendi esteri: ritenuta+credito. Minusvalenze compensabili 4 anni. IVAFE 0.2%. Crypto 33% dal 2026.

═══ REGOLE ═══
1. Italiano, pragmatico, diretto — mai generico o teorico
2. SEMPRE pro E contro con dati — "rischio alto" non basta, specifica beta/drawdown/scenario
3. Mai garantire rendimenti. Mai "consulenza" — usa "analisi", "valutazione", "spunto operativo"
4. Se dati insufficienti → dichiaralo, indica cosa manca
5. Includi ISIN SOLO se lo trovi da fonte web verificata. NON inventare o indovinare codici ISIN — se non lo trovi, ometti il campo
6. Domande generiche ("cosa comprare?", "idee?") → proponi subito le 2-3 migliori opportunità AZIONABILI ORA (vedi regola 10) in base a probabilità rischio/rendimento, profilo investitore e contesto macro attuale. Mix Italia+estero, diversi settori. Mai chiedere chiarimenti — agisci con la migliore proposta possibile.
7. PREZZI: se il titolo è nel PORTAFOGLIO ATTUALE, usa il prezzo di carico e i dati già forniti nel contesto. Cerca via web SOLO il prezzo corrente e le news. NON inventare prezzi o dati — se non trovi un dato, scrivi "n/d".
8. ACCURATEZZA: verifica via web il ticker esatto e il nome completo dell'azienda. Per titoli Borsa Italiana usa il suffisso .MI (es. NXT.MI). Non confondere aziende con nomi simili.
9. Disclaimer SOLO al PRIMO messaggio della conversazione (mai ripeterlo):
⚠️ AVVERTENZA: Informazioni a scopo informativo/educativo. Non consulenza ex D.Lgs. 58/1998 (TUF). L'utente è unico responsabile. Rendimenti passati non indicativi dei futuri.
10. COERENZA PROPOSTE/TIMING — regola critica:
   - Proponi SOLO titoli con segnale tecnico BUY NOW o ACCUMULATE NOW. Mai proporre un titolo se il timing dice "attendere".
   - Per OGNI proposta specifica timing operativo: "comprare ora a X" / "accumulare tra X-Y" / "entrare al breakout di X".
   - Se un titolo ha fondamentali eccellenti ma tecnici negativi → NON proporlo. Mettilo in una nota "📋 Da monitorare" separata con livello di entrata target.
   - MAI contraddire: se nella stessa analisi scrivi "wait"/"attendere"/"aspettare" su un titolo, quel titolo NON deve essere nelle tue proposte.
   - Regola d'oro: ogni titolo proposto = entry chiaro + stop + target. Se non puoi dare entry, non proporlo.
11. MEMORIA: NON hai memoria delle conversazioni precedenti. Non fingere di ricordare analisi passate. Ogni sessione è indipendente — basa le analisi SOLO sui dati forniti nel contesto attuale (portafoglio, profilo, dati web).`;

// ─── 13 DOMANDE PROFILAZIONE (MiFID II aligned) ─────────────
export const PROFILE_QUESTIONS = [
  // === CAPACITÀ OGGETTIVA (50% peso) ===
  {
    key: "age",
    q: "Qual è la tua fascia d'età?",
    type: "single",
    category: "objective",
    opts: ["18-30", "31-40", "41-50", "51-60", "60+"]
  },
  {
    key: "income",
    q: "Qual è il tuo reddito annuo lordo approssimativo?",
    type: "single",
    category: "objective",
    opts: ["< €25.000", "€25.000 - €50.000", "€50.000 - €100.000", "> €100.000"]
  },
  {
    key: "savings",
    q: "Quanto riesci a risparmiare mediamente al mese?",
    type: "single",
    category: "objective",
    opts: ["< €200", "€200 - €500", "€500 - €1.000", "> €1.000"]
  },
  {
    key: "capital",
    q: "Quanto capitale vuoi dedicare agli investimenti?",
    type: "single",
    category: "objective",
    opts: ["< €5.000", "€5.000 - €20.000", "€20.000 - €50.000", "€50.000 - €100.000", "> €100.000"]
  },
  {
    key: "horizon",
    q: "Qual è il tuo orizzonte temporale?",
    type: "single",
    category: "objective",
    opts: ["< 1 anno", "1-3 anni", "3-5 anni", "5-10 anni", "10+ anni"]
  },
  {
    key: "goal",
    q: "Qual è il tuo obiettivo principale?",
    type: "single",
    category: "objective",
    opts: [
      "Fondo emergenza / liquidità",
      "Acquisto casa",
      "Crescita del capitale",
      "Reddito passivo (dividendi)",
      "Pensione integrativa",
      "Educazione figli"
    ]
  },
  // === DISPONIBILITÀ SOGGETTIVA (40% peso) ===
  {
    key: "experience",
    q: "Qual è la tua esperienza con gli investimenti finanziari?",
    type: "single",
    category: "subjective",
    opts: [
      "Mai investito — sono alle prime armi",
      "Qualche esperienza — ho investito in fondi o ETF",
      "Esperto — investo attivamente in azioni, obbligazioni, derivati"
    ]
  },
  {
    key: "knowledgeCheck",
    q: "Sei consapevole che gli investimenti in azioni possono perdere valore, anche significativamente?",
    type: "single",
    category: "subjective",
    opts: [
      "Sì, lo capisco e lo accetto",
      "Lo capisco in teoria, ma mi preoccupa",
      "Non ne sono sicuro/a"
    ]
  },
  {
    key: "scenarioDrop",
    q: "Scenario: il tuo portafoglio perde il 20% in un mese. Cosa faresti?",
    type: "single",
    category: "subjective",
    opts: [
      "Compro di più — è un'opportunità",
      "Mantengo e aspetto la ripresa",
      "Vendo parte per limitare le perdite",
      "Vendo tutto — non posso sopportarlo"
    ]
  },
  {
    key: "scenarioGamble",
    q: "Preferiresti...",
    type: "single",
    category: "subjective",
    opts: [
      "€1.000 garantiti subito",
      "50% di probabilità di ricevere €2.500 (o niente)"
    ]
  },
  {
    key: "maxLoss",
    q: "Qual è la perdita massima annuale che saresti disposto/a ad accettare?",
    type: "single",
    category: "subjective",
    opts: ["Max 5%", "Max 10%", "Max 20%", "Max 30%", "Oltre 30% — se il potenziale è alto"]
  },
  // === SOSTENIBILITÀ (10% peso) ===
  {
    key: "esg",
    q: "Quanto è importante per te che i tuoi investimenti siano sostenibili (ESG)?",
    type: "single",
    category: "sustainability",
    opts: [
      "Molto importante — priorità assoluta",
      "Preferibile ma non vincolante",
      "Indifferente — conta solo il rendimento"
    ]
  },
  // === SETTORI (extra — non pesa sul profilo rischio) ===
  {
    key: "sectors",
    q: "Quali settori ti interessano? (seleziona uno o più)",
    type: "multi",
    category: "preference"
  }
];

// ─── LOCALSTORAGE KEYS ──────────────────────────────────────
export const LS_KEYS = {
  WATCHLIST: "sm_watchlist",
  PROFILE: "sm_profile",
  PORTFOLIO: "sm_portfolio",
  CHAT: "sm_advisor_chat",
  ALERTS: "sm_alerts",
  API_MONITOR: "sm_api_monitor",
  FUNDS: "sm_portfolio_funds",

  INTELLIGENCE: "sm_intelligence_report",
  INTELLIGENCE_FREQ: "sm_intelligence_frequency",
  INTELLIGENCE_CHAT: "sm_intelligence_chat",
  RECOVERY_DATA: "sm_recovery_data",

  RISK_CACHE: "sm_risk_cache",
  MC_CACHE: "sm_mc_cache",
  TRANSACTIONS: "sm_transactions"
};

// ─── SKILL: CONFRONTO TECNICO ────────────────────────────────
export const COMPARISON_PROMPT = `═══ MODALITÀ CONFRONTO ═══

OBBLIGATORIO: genera una tabella comparativa STRUTTURATA. Nessun testo narrativo prima della tabella.

Web search strategy (max 4):
1. Geopolitica/macro: "stock market today geopolitical risks {year}"
2. Confronto diretto: "{ticker1} vs {ticker2} stock comparison {year}"
3. Se >2 titoli: "{ticker3} stock analysis P/E forward {year}"
4. Borsa Italiana (se applicabile): "{azienda} analisi target Piazza Affari {year}"

TABELLA (obbligatoria, tutte le righe per tutti i titoli):

| Metrica | {T1} | {T2} | ... |
|---------|------|------|-----|
| Prezzo attuale | | | |
| P/E trailing | | | |
| P/E forward | | | |
| PEG | | | |
| Revenue Growth YoY | | | |
| Margine operativo | | | |
| FCF Yield | | | |
| Dividend Yield | | | |
| Beta | | | |
| 📊 Fondamentali (1-10) | | | |
| 📈 Tecnico | 🟢/🟡/🔴 | | |
| 📰 Sentiment | 🟢/🟡/🔴 | | |
| 🌍 Macro | 🟢/🟡/🔴 | | |
| ⚠️ Rischio | 🟢/🟡/🔴 | | |
| 🎯 Profilo | 🟢/🟡/🔴 | | |

🏆 **VERDETTO**: {SYMBOL} — {motivazione con 3 dati numerici comparativi}
🎯 **Entry/Stop/Target**: per il titolo vincente
📋 **Da monitorare**: titoli con fondamentali OK ma timing non ora`;

// ─── SKILL: SCREENER V0 ─────────────────────────────────────
export const SCREENER_PROMPT = `═══ MODALITÀ SCREENER ═══

L'utente cerca titoli con criteri specifici. NON proporre titoli a memoria — CERCA dati reali.

Web search strategy (tutte e 4 le search):
1. Screener: "{criteri} stocks screener {year}" — cerca risultati da FinViz, TradingView, MarketWatch
2. Ranking: "best {settore} stocks {criterio} {year}" — articoli analyst con ranking
3. Conferma: "{top ticker} stock analysis P/E dividend {year}" — verifica top pick
4. Italia (se richiesto): "migliori azioni {settore} Borsa Italiana {year} screener"

OUTPUT OBBLIGATORIO:

### Risultati Screener: {criteri utente}
| # | Ticker | Nome | Settore | Prezzo | P/E fwd | Rev Gr% | Div% | Margine | Score |
|---|--------|------|---------|--------|---------|---------|------|---------|-------|
(max 8 titoli, ordinati per Score decrescente)

Score = media ponderata criteri richiesti (1-10). Spiega formula sotto la tabella.

### Deep Analysis — Top 3

Per ciascuno dei top 3: tabella 6 dimensioni compatta (formato standard) + entry/stop/target.
Max 100 parole per titolo.

REGOLE:
- Includi almeno 2 mid/small cap se disponibili — non solo mega cap
- Ogni dato DEVE provenire da web search, non da memoria
- Se un criterio non è verificabile via web → scrivi "n/d"
- Applica Rule 10 (coerenza timing): se un titolo ha tecnici negativi → Da Monitorare, non proporre`;

// ─── SKILL: ALERT SCANNER V0 ────────────────────────────────
export const ALERT_SCANNER_PROMPT = `═══ MODALITÀ ALERT SCANNER ═══

L'utente vuole un'analisi degli alert tecnici attivi. Il contesto alert è fornito sotto.

Per OGNI alert con priority 1-2:
1. Web search: "{ticker} technical analysis {indicator} {year}"
2. Conferma/smentisci il segnale con dati web
3. Suggerisci azione: 🟢 BUY | 🟡 HOLD | 🔴 SELL | ⏳ MONITOR

OUTPUT:
| Alert | Segnale | Conferma Web | Azione |
|-------|---------|--------------|--------|

💡 SINTESI: {1-2 frasi con priorità azione}

Se non ci sono alert attivi: "Nessun alert attivo per {SYMBOL}. Situazione tecnica neutrale." + breve analisi tecnica generale.`;

// ─── MISC ───────────────────────────────────────────────────
export const MAX_CHAT_PERSIST = 50;
